/* ==========================================================================
   Home -- post a request, and edit a live one
   Same form both ways. The hours advisor comments; it never sets the number.
   ========================================================================== */

import {
  Card, Field, Note, Pick, SectionHead, Slider, Stepper, Switch,
} from '../../components/ui';
import { DOW, PAY_METHODS } from '../../data/catalog';
import { CLEANERS } from '../../data/seed';
import { house, jobLabel, suggestHours } from '../../lib/domain';
import {
  SLOT_MINUTES, TODAY_ISO, addDays, dayLong, fmtTime, money, nowSlot, parseDate,
} from '../../lib/time';
import { t } from '../../lib/i18n';
import type { Draft } from '../../store';
import {
  activeHouse, back, blankDraft, commit, nav, postRequest, saveEditedRequest, state,
  toggleIn,
} from '../../store';

/** What the cleaners near this home actually ask, from their profiles.

    This used to be a sentence typed into the app -- "$25-$45, below $26 you
    will get fewer applicants" -- which was never worked out from anybody
    and stopped being anywhere near true once every rate was reset to $15.
    Fewer than three cleaners nearby is too few to say anything general, so
    then it says nothing. */
const NEAR_MILES = 10;
function rateNote(rate: number): string | null {
  const asks = CLEANERS
    .filter((c) => c.visible !== false && c.milesAway !== null && c.milesAway <= NEAR_MILES && c.rate > 0)
    .map((c) => c.rate)
    .sort((a, b) => a - b);
  if (asks.length < 3) return null;
  const es = state.lang === 'es';
  const lo = asks[0], hi = asks[asks.length - 1];
  const range = lo === hi ? money(lo) : money(lo) + '–' + money(hi);
  const said = es
    ? `${asks.length} limpiadores cerca de usted piden ${range} la hora.`
    : `${asks.length} cleaners near you ask ${range} an hour.`;
  if (rate < lo) {
    return said + (es
      ? ' Con esta tarifa está por debajo de todos, así que espere menos respuestas.'
      : ' At this rate you are below all of them, so expect fewer replies.');
  }
  if (rate >= hi) {
    return said + (es
      ? ' Esta tarifa iguala o supera lo que pide cada uno.'
      : ' This rate meets or beats what every one of them asks.');
  }
  const covered = asks.filter((a) => a <= rate).length;
  return said + (es
    ? ` ${covered} de ellos piden ${money(rate)} o menos.`
    : ` ${covered} of them ask ${money(rate)} or less.`);
}

export function PostRequest() {
  if (!state.draft) state.draft = blankDraft(activeHouse().id);
  return <Form d={state.draft} isEdit={false} />;
}

export function EditRequest() {
  if (!state.draft) return null;
  return <Form d={state.draft} isEdit />;
}

function Form({ d, isEdit }: { d: Draft; isEdit: boolean }) {
  const h = house(d.houseId);
  const suggested = suggestHours(d.jobIds, h);
  const gap = d.jobIds.length ? suggested - d.hours : 0;
  const set = (k: keyof Draft, v: unknown) => { (d as any)[k] = v; commit(); };

  /* Whether what is in the form right now IS the next quarter hour. Computed
     rather than remembered as a flag: a flag would still say "Now" ten
     minutes later, when it had stopped being true. */
  const slotNow = nowSlot();
  const isNow = d.date === slotNow.date && d.start === slotNow.start;

  return (
    <div className="screen-pad formgrid">
      {isEdit ? (
        <Note warm>
          {t('pr_editing')}
        </Note>
      ) : (
        <Note>
          Your home stays hidden from cleaners until you post. While this is live they see
          your area, home size, rating and the jobs you tick — never your address.
        </Note>
      )}

      {/* No property picker here. Which home this is about was decided in the
          app bar before you got here, and the title bar says so; asking again
          made a form about one house look like a form about all of them. */}

      <Card>
        <SectionHead
          title={t('h_whichjobs')}
          action="Edit my list"
          onAction={() => nav('h_joblist')}
        />
        <div className="chips">
          {h.jobList.map((j) => (
            <Pick
              key={j.jobId}
              label={jobLabel(j.jobId)}
              on={d.jobIds.includes(j.jobId)}
              onClick={() => { toggleIn(d.jobIds, j.jobId); commit(); }}
            />
          ))}
        </div>
        {d.jobIds.length === 0 && (
          <span className="hint">{t('pr_pickone')}</span>
        )}
      </Card>

      <Card>
        <SectionHead title={t('when')} />

        {/* Somebody posting because the guests arrive in an hour should not
            have to work out what time it is and type it in. Rounded up to
            the next quarter hour, because nobody schedules to the minute and
            a start time already in the past is not a start time. */}
        <div className="chips">
          <Pick
            label={isNow ? 'Now — ' + fmtTime(d.start) : 'Now'}
            on={isNow}
            onClick={() => {
              const slot = nowSlot();
              set('date', slot.date);
              set('start', slot.start);
            }}
          />
          <Pick
            label={t('today')}
            on={d.date === TODAY_ISO && !isNow}
            onClick={() => set('date', TODAY_ISO)}
          />
          <Pick
            label={t('tomorrow')}
            on={d.date === addDays(TODAY_ISO, 1)}
            onClick={() => set('date', addDays(TODAY_ISO, 1))}
          />
        </div>

        <Field label="Date">
          <input
            className="input"
            type="date"
            value={d.date}
            onChange={(e) => set('date', e.target.value)}
          />
        </Field>
        <div className="field-row">
          <Field label={t('pr_start')}>
            <input
              className="input"
              type="time"
              step={SLOT_MINUTES * 60}
              value={d.start}
              onChange={(e) => set('start', e.target.value)}
            />
          </Field>
          <Field label={t('hours')}>
            <Stepper
              value={d.hours}
              onStep={(delta) => set('hours', Math.max(1, d.hours + delta * 0.5))}
            />
          </Field>
        </div>

        {d.jobIds.length > 0 && (
          <Note warm={Math.abs(gap) > 1.5}>
            {gap > 1.5 ? (
              <>
                <b>{t('pr_tight')}</b> For {d.jobIds.length} jobs in a {h.bedrooms}-bed{' '}
                {h.homeType.toLowerCase()} we would estimate about {suggested} hours. You can
                still post {d.hours} — just say which jobs matter most.
              </>
            ) : gap < -1.5 ? (
              <>
                You have allowed {d.hours} hours for a list we estimate at {suggested}. Plenty
                of room, or add another job.
              </>
            ) : (
              <>{d.hours} hours looks about right for this list.</>
            )}
          </Note>
        )}

        <div className="switchrow">
          <div className="col">
            <b style={{ fontSize: 14 }}>{t('pr_regular')}</b>
            <span className="hint">{t('pr_sameweek')}</span>
          </div>
          <Switch
            on={!!d.recurring}
            onClick={() =>
              set('recurring', d.recurring
                ? null
                : { freq: 'weekly', day: DOW[parseDate(d.date).getDay()], openEnded: true })
            }
          />
        </div>
        {d.recurring && (
          <Note>
            A standing {dayLong(DOW[parseDate(d.date).getDay()])} slot, held until either of
            you ends it.
          </Note>
        )}
      </Card>

      <Card>
        <SectionHead title={t('pay')} />
        <Field label={t('pr_rateperhour') + money(d.rate)}>
          <Slider value={d.rate} onChange={(v) => set('rate', v)} />
        </Field>
        <div className="row between">
          <span className="sub">{t('total')} — {d.hours} {t('hours')}</span>
          <span className="rate">{money(d.rate * d.hours)}</span>
        </div>
        {rateNote(d.rate) && <Note>{rateNote(d.rate)}</Note>}
        <Field label={t('pr_howpay')}>
          <div className="chips">
            {PAY_METHODS.map((p) => (
              <Pick
                key={p.id}
                label={p.label}
                on={d.payMethods.includes(p.id)}
                onClick={() => { toggleIn(d.payMethods, p.id); commit(); }}
              />
            ))}
          </div>
        </Field>
        <span className="hint">
          {t('pr_nomoney')}
        </span>
      </Card>

      <Card>
        <SectionHead title={t('h_anythingelse')} />
        <textarea
          className="textarea"
          placeholder={t('pr_notesph')}
          value={d.notes}
          onChange={(e) => set('notes', e.target.value)}
        />
      </Card>

      <button
        type="button"
        className="btn lg block"
        disabled={d.jobIds.length === 0}
        onClick={isEdit ? saveEditedRequest : postRequest}
      >
        {isEdit ? t('b_save') : t('b_post')}
      </button>
      {isEdit && (
        <button type="button" className="btn quiet block" onClick={back}>
          {t('b_cancel')}
        </button>
      )}
    </div>
  );
}
